import { Paper, Table, Text, Title } from "@mantine/core";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import "../Report.scss";
import useReportStore from "../../../store/useReportStore";
import { JobPositionData, SkillsAssessment } from "../../../types/api";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mantine/hooks";
import { useState } from "react";
import { getJobDetailsByTitle } from "../../../api/reportApi";
import { JobDetailsModal } from "../../../utils/components/JobDetailsModal";
import useProfileStore from "../../../store/useProfileStore";


interface SkillAssessment {
  title: {
    text: string;
    color: string;
  };
  percentage: number;
  skills: string;
  master: string;
}

const transformSkillsAssessment = (
  skillsAssessments: SkillsAssessment[]
): { skills_assessment: SkillAssessment[] } => {
  const colors = ["#05D8D2", "#3A8BD9", "#6F40E2", "#A64DD7", "#DE57CC"];

  return {
    skills_assessment: skillsAssessments.map((assessment, index) => ({
      title: {
        text: assessment?.title ?? "",
        color: colors[index % colors.length],
      },
      percentage: assessment?.fit_percentage ?? 0,
      skills: Array.isArray(assessment?.skills_you_have)
        ? assessment.skills_you_have.join(", ")
        : "",
      master: Array.isArray(assessment?.skills_to_master)
        ? assessment.skills_to_master.join(", ")
        : "",
    })),
  };
};

export const SkillsAssessmentComponent = () => {
  const matches = useMediaQuery("(max-width: 1100px)");

  const { report, reportId } = useReportStore();
  const { t } = useTranslation();

  const content = report?.skills_assessment || [];

  const transformedContent = transformSkillsAssessment(content.data);
  const { profileLanguage } = useProfileStore();
  const [opened, setOpened] = useState(false);
  const [currentJob, setCurrentJob] = useState<JobPositionData | null>(null);

  const handleOpen = async (title: string) => {
    try {
      const res = await getJobDetailsByTitle(
        title,
        Object.keys(profileLanguage!)[0]
      );
      if (res) {
        setCurrentJob(res.data);
        setOpened(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const rows = transformedContent.skills_assessment.map((element, index) => (
    <Table.Tr key={index}>
      <Table.Td p={16}>
        <Paper
          bg={element.title.color}
          px={16}
          py={8}
          radius={16}
          c="#fff"
          w="max-content"
        >
          <Text
            fw="bold"
            style={{ cursor: "pointer" }}
            onClick={() => {
              handleOpen(element.title.text);
            }}
          >
            {element.title.text}
          </Text>
        </Paper>
      </Table.Td>
      <Table.Td p={14}>
        <Text>{element.percentage}%</Text>
      </Table.Td>
      <Table.Td p={14}>
        <Text>{element.skills}</Text>
      </Table.Td>
      <Table.Td p={14}>
        <Text>{element.master}</Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Paper radius="24px" p="32px" mt={32} withBorder>
      <Title size={matches ? 24 : 36} mb={8}>
        {content?.mark_down ? content?.mark_down.replace("##", "") : " "}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {content?.explanation}
      </Text>
      <Paper radius="16px" withBorder className="x-auto">
        <Table highlightOnHover withColumnBorders miw={1056}>
          <Table.Thead>
            <Table.Tr p={16}>
              <Table.Th>
                <Text fw="bold">{t("jobTitle")}</Text>
              </Table.Th>
              <Table.Th>
                <Text fw="bold">{t("fitPercentage")}</Text>
              </Table.Th>
              <Table.Th>
                <Text fw="bold">{t("skillsYouHave")}</Text>
              </Table.Th>
              <Table.Th>
                <Text fw="bold">{t("skillsToMaster")}</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{rows}</Table.Tbody>
        </Table>
      </Paper>
      <JobDetailsModal
        opened={opened}
        job={currentJob!}
        onClose={() => {
          setOpened(false);
          setCurrentJob(null);
        }}
        setCurrentJob={setCurrentJob}
      />
      <LikeResponse report_id={reportId} section="skills_assessment" />
    </Paper>
  );
};
